import { createContext, useContext, useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth.js';
import { Icon, Logo } from '../components/Icon.js';
import { ProfileSwitcher } from '../components/ProfileSwitcher.js';
import { BRAND } from '../branding.js';
import { ROLE_LABEL, isAdminLevel } from '../utils/roles.js';

interface NavItem {
  key: string;
  label: string;
  icon: string;
  to: string;
  mock?: boolean;
  adminOnly?: boolean;
}

const NAV: NavItem[] = [
  { key: 'dashboard', label: 'Дашборд', icon: 'dashboard', to: '/admin/dashboard', mock: true },
  { key: 'products', label: 'Продукты', icon: 'products', to: '/admin/products' },
  { key: 'flows', label: 'Флоу', icon: 'refresh', to: '/admin/flows' },
  { key: 'subscriptions', label: 'Подписки', icon: 'users', to: '/admin/subscriptions' },
  { key: 'payments', label: 'Платежи', icon: 'payments', to: '/admin/payments', mock: true },
  { key: 'promo-codes', label: 'Промокоды', icon: 'audit', to: '/admin/promo-codes' },
  { key: 'comp-tickets', label: 'Промобилеты', icon: 'audit', to: '/admin/comp-tickets' },
  { key: 'profiles', label: 'Настройки', icon: 'settings', to: '/admin/settings', adminOnly: true },
  { key: 'users', label: 'Пользователи', icon: 'users', to: '/admin/users', adminOnly: true },
  { key: 'audit', label: 'Аудит-лог', icon: 'audit', to: '/admin/audit', mock: true },
];

const SIDEBAR_WIDTH = 232;
const SIDEBAR_WIDTH_COLLAPSED = 60;

/**
 * Подменю в главном меню: страница регистрирует свои секции, и они
 * появляются под её пунктом (например, под «Профили» — секции профиля).
 * Снимается при размонтировании страницы.
 */
export interface Subnav {
  /** Пункт главного меню, под которым показать подпункты (ключ из NAV). */
  under: string;
  items: { id: string; label: string }[];
  activeId: string;
  onSelect: (id: string) => void;
}

const SubnavCtx = createContext<{ set: (s: Subnav | null) => void } | null>(null);

export function useSubnav(subnav: Subnav | null): void {
  const ctx = useContext(SubnavCtx);
  // Страница пересоздаёт объект на каждый рендер; обновляем меню только
  // когда реально изменилось, что показывать.
  const key = subnav
    ? `${subnav.under}|${subnav.activeId}|${subnav.items.map((i) => `${i.id}:${i.label}`).join(',')}`
    : '';
  useEffect(() => {
    if (!ctx) return;
    ctx.set(subnav);
    return () => ctx.set(null);
  }, [ctx, key]); // eslint-disable-line react-hooks/exhaustive-deps -- key покрывает subnav
}

export function AdminLayout(): JSX.Element {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem('sidebar-collapsed') === '1',
  );
  const [subnav, setSubnav] = useState<Subnav | null>(null);
  const subnavApi = useMemo(() => ({ set: setSubnav }), []);

  useEffect(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  if (loading) {
    return <div style={{ padding: 32, color: 'var(--fg-subtle)' }}>Загрузка…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;

  const toggleTheme = (): void => {
    const cur = document.documentElement.getAttribute('data-theme');
    if (cur === 'dark') {
      document.documentElement.removeAttribute('data-theme');
      localStorage.removeItem('theme');
    } else {
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  // Высота корня строго в экран: скроллится область каждой страницы (у них
  // overflowY: auto), а боковое меню стоит на месте. С minHeight корень
  // растягивался под контент, и всю админку скроллило окно.
  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>
      <aside
        style={{
          position: 'relative',
          width,
          flexShrink: 0,
          borderRight: '1px solid var(--hairline)',
          background: 'var(--bg)',
          display: 'flex',
          flexDirection: 'column',
          padding: collapsed ? '18px 8px' : '18px 14px',
          gap: 6,
          transition: 'width 180ms ease, padding 180ms ease',
        }}
      >
        <Link
          to="/admin/products"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: collapsed ? '2px 0 22px' : '2px 8px 22px',
            justifyContent: collapsed ? 'center' : 'flex-start',
            color: 'inherit',
            textDecoration: 'none',
          }}
        >
          <Logo size={24} />
          {!collapsed && (
            <div style={{ fontWeight: 600, fontSize: 15, letterSpacing: -0.2 }}>{BRAND.name}</div>
          )}
        </Link>

        <ProfileSwitcher collapsed={collapsed} />

        {NAV.filter((n) => !n.adminOnly || isAdminLevel(user.role)).map((n) => (
          <NavLink
            key={n.key}
            to={n.to}
            title={collapsed ? n.label : undefined}
            style={({ isActive }) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: collapsed ? '8px 0' : '8px 10px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              background: isActive ? 'var(--bg-sunken)' : 'transparent',
              color: isActive ? 'var(--fg)' : 'var(--fg-muted)',
              borderRadius: 'var(--radius)',
              fontSize: 'var(--fs-base)',
              fontWeight: 500,
              width: '100%',
              opacity: n.mock ? 0.45 : 1,
            })}
          >
            <Icon name={n.icon} size={16} />
            {!collapsed && (
              <>
                <span style={{ flex: 1 }}>{n.label}</span>
                {n.mock && <span className="mock-badge">mock</span>}
              </>
            )}
          </NavLink>
        )).flatMap((link, i) => {
          const n = NAV.filter((x) => !x.adminOnly || isAdminLevel(user.role))[i]!;
          if (collapsed || !subnav || subnav.under !== n.key) return [link];
          return [
            link,
            <div key={`${n.key}-sub`} className="subnav">
              {subnav.items.map((it) => (
                <a
                  key={it.id}
                  href={`#${it.id}`}
                  className={it.id === subnav.activeId ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    subnav.onSelect(it.id);
                  }}
                >
                  {it.label}
                </a>
              ))}
            </div>,
          ];
        })}

        <div style={{ flex: 1 }} />

        <button
          className="btn btn-sm btn-ghost"
          onClick={toggleTheme}
          style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
          type="button"
          title={collapsed ? 'Сменить тему' : undefined}
        >
          <Icon name="moon" size={14} />
          {!collapsed && 'Сменить тему'}
        </button>

        {collapsed ? (
          <button
            className="btn btn-sm btn-ghost"
            type="button"
            title={user.email}
            onClick={async () => {
              await logout();
              nav('/login');
            }}
            style={{
              justifyContent: 'center',
              padding: 6,
              width: '100%',
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'var(--accent-subtle-bg)',
                color: 'var(--accent-subtle-fg)',
                display: 'grid',
                placeItems: 'center',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {(user.name || user.email)[0]?.toUpperCase() ?? '?'}
            </div>
          </button>
        ) : (
          <div
            style={{
              padding: 10,
              borderRadius: 'var(--radius)',
              background: 'var(--bg-subtle)',
              border: '1px solid var(--hairline)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'var(--accent-subtle-bg)',
                color: 'var(--accent-subtle-fg)',
                display: 'grid',
                placeItems: 'center',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {(user.name || user.email)[0]?.toUpperCase() ?? '?'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <bdi>{user.name || user.email}</bdi>
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: 'var(--fg-subtle)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user.name ? user.email : ROLE_LABEL[user.role]}
              </div>
            </div>
            <button
              className="btn btn-sm btn-ghost"
              title="Выйти"
              type="button"
              onClick={async () => {
                await logout();
                nav('/login');
              }}
            >
              <Icon name="logout" size={14} />
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          style={{
            position: 'absolute',
            top: '50%',
            right: -12,
            transform: 'translateY(-50%)',
            width: 24,
            height: 24,
            borderRadius: '50%',
            border: '1px solid var(--hairline)',
            background: 'var(--bg)',
            color: 'var(--fg-muted)',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            padding: 0,
            zIndex: 10,
            boxShadow: 'var(--shadow-sm, 0 1px 2px rgba(0,0,0,0.06))',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 180ms ease',
            }}
          >
            <Icon name="chevron" size={12} />
          </span>
        </button>
      </aside>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <SubnavCtx.Provider value={subnavApi}>
          <Outlet />
        </SubnavCtx.Provider>
      </div>
    </div>
  );
}

export function Topbar({
  title,
  subtitle,
  actions,
  mock,
}: {
  // ReactNode, а не строка: пользовательский текст в заголовке оборачивают
  // в <bdi> (см. utils/bidi.ts).
  title: ReactNode;
  subtitle?: string;
  actions?: JSX.Element;
  mock?: boolean;
}): JSX.Element {
  return (
    <header
      style={{
        height: 60,
        flexShrink: 0,
        padding: '0 28px',
        borderBottom: '1px solid var(--hairline)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 20,
        background: 'var(--bg)',
      }}
    >
      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
        <h1 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: -0.3 }}>{title}</h1>
        {subtitle && <span style={{ color: 'var(--fg-subtle)', fontSize: 13 }}>{subtitle}</span>}
        {mock && <span className="mock-badge" style={{ height: 17 }}>mock</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{actions}</div>
    </header>
  );
}
